import type {
    Callbacks,
    FieldData,
    FieldEntity,
    FieldError,
    FormInstance,
    InternalHooks,
    NamePath,
    Rule,
    Store,
    ValidateMessages,
} from './types';

type Listener = () => void;

interface FieldMeta {
    touched: boolean;
    validating: boolean;
    errors: string[];
    validateSeq: number;
}

export class FormStore {
    private store: Store = {};
    private fieldEntities: FieldEntity[] = [];
    private initialValues: Store = {};
    private callbacks: Callbacks = {};
    private validateMessages?: ValidateMessages;
    private preserve: boolean = true;
    private scrollToFirstError: boolean = false;
    private fieldListeners: Map<string, Set<Listener>> = new Map();
    private fieldMeta: Record<string, FieldMeta> = {};
    private externalErrorFields: Set<string> = new Set();
    private checkingFields: Set<string> = new Set();

    constructor(forceRootUpdate: () => void) {
        this.forceRootUpdate = forceRootUpdate;
    }

    private forceRootUpdate: () => void;

    getForm = (): FormInstance => {
        return {
            getFieldValue: this.getFieldValue,
            getFieldsValue: this.getFieldsValue,
            getFieldError: this.getFieldError,
            getFieldsError: this.getFieldsError,
            isFieldsTouched: this.isFieldsTouched,
            isFieldTouched: this.isFieldTouched,
            isFieldValidating: this.isFieldValidating,
            resetFields: this.resetFields,
            setFields: this.setFields,
            setFieldValue: this.setFieldValue,
            setFieldsValue: this.setFieldsValue,
            validateFields: this.validateFields,
            submit: this.submit,
            getInternalHooks: this.getInternalHooks,
            scrollToField: this.scrollToField,
            setExternalErrors: this.setExternalErrors,
            setFieldsChecking: this.setFieldsChecking,
            clearFieldsChecking: this.clearFieldsChecking,
            getRegisteredFieldNames: this.getRegisteredFieldNames,
        };
    };

    private setExternalErrors = (fields: FieldError[]) => {
        const affected = new Set<string>();
        const nextExternal = new Set<string>();

        fields.forEach(field => {
            const nameStr = this.getNamePathStr(field.name);
            nextExternal.add(nameStr);
            affected.add(nameStr);

            const meta = this.getFieldMeta(field.name);
            meta.errors = field.errors;
            meta.touched = true;
            meta.validating = false;
        });

        this.externalErrorFields.forEach(nameStr => {
            if (nextExternal.has(nameStr)) return;
            affected.add(nameStr);
            this.getFieldMeta(nameStr.split('.')).errors = [];
        });

        this.checkingFields.forEach(nameStr => {
            affected.add(nameStr);
            this.getFieldMeta(nameStr.split('.')).validating = false;
        });
        this.checkingFields = new Set();
        this.externalErrorFields = nextExternal;

        affected.forEach(nameStr => this.notifyField(nameStr.split('.')));
        this.forceRootUpdate();
    };

    private setFieldsChecking = (names: NamePath[]) => {
        names.forEach(name => {
            const nameStr = this.getNamePathStr(name);
            this.checkingFields.add(nameStr);
            this.getFieldMeta(name).validating = true;
            this.notifyField(name);
        });
        this.forceRootUpdate();
    };

    private clearFieldsChecking = () => {
        if (this.checkingFields.size === 0) return;

        const cleared = this.checkingFields;
        this.checkingFields = new Set();
        cleared.forEach(nameStr => {
            this.getFieldMeta(nameStr.split('.')).validating = false;
            this.notifyField(nameStr.split('.'));
        });
        this.forceRootUpdate();
    };

    private getInternalHooks = (key: string): InternalHooks | null => {
        if (key === 'FORM_INTERNAL_HOOKS') {
            return {
                registerField: this.registerField,
                setInitialValues: this.setInitialValues,
                setCallbacks: this.setCallbacks,
                getFields: this.getFields,
                setValidateMessages: this.setValidateMessages,
                setPreserve: this.setPreserve,
                subscribe: this.subscribe,
                unregisterField: this.unregisterField,
                setScrollToFirstError: (value: boolean) => {
                    this.scrollToFirstError = value;
                },
            };
        }
        return null;
    };

    private setInitialValues = (initialValues: Store, init: boolean) => {
        this.initialValues = initialValues || {};
        if (init) {
            this.store = { ...this.initialValues, ...this.store };
        }
    };

    private getFieldValue = (name: NamePath) => {
        const nameStr = this.getNamePathStr(name);
        return this.getValueFromStore(nameStr, this.store);
    };

    private getFieldsValue = (nameList?: NamePath[] | true) => {
        if (nameList === true || !nameList) {
            return { ...this.store };
        }
        const values: Store = {};
        nameList.forEach((name) => {
            const nameStr = this.getNamePathStr(name);
            const value = this.getValueFromStore(nameStr, this.store);
            if (value !== undefined) {
                this.setValueToStore(nameStr, value, values);
            }
        });
        return values;
    };

    private getFieldError = (name: NamePath): string[] => {
        return this.getFieldMeta(name).errors || [];
    };

    private getFieldsError = (nameList?: NamePath[]) => {
        const fields = nameList || this.fieldEntities.map(e => e.getNamePath());
        return fields.map(name => ({
            name,
            errors: this.getFieldError(name),
        }));
    };

    private isFieldsTouched = (nameList?: NamePath[], allFieldsTouched?: boolean) => {
        const fields = nameList || this.fieldEntities.map(e => e.getNamePath());
        if (allFieldsTouched) {
            return fields.every(name => this.isFieldTouched(name));
        }
        return fields.some(name => this.isFieldTouched(name));
    };

    private isFieldTouched = (name: NamePath) => {
        return this.getFieldMeta(name).touched || false;
    };

    private isFieldValidating = (name: NamePath) => {
        return this.getFieldMeta(name).validating || false;
    };

    private resetFields = (nameList?: NamePath[]) => {
        const fields = nameList || this.fieldEntities.map(e => e.getNamePath());

        fields.forEach(name => {
            const nameStr = this.getNamePathStr(name);
            const initialValue = this.getValueFromStore(nameStr, this.initialValues);
            
            if (initialValue !== undefined) {
                this.setValueToStore(nameStr, initialValue, this.store);
            } else {
                this.deleteValueFromStore(nameStr, this.store);
            }

            const meta = this.getFieldMeta(name);
            meta.touched = false;
            meta.errors = [];
            meta.validating = false;
        });

        if (nameList) {
            nameList.forEach(name => {
                const nameStr = this.getNamePathStr(name);
                this.externalErrorFields.delete(nameStr);
                this.checkingFields.delete(nameStr);
                this.notifyField(name);
            });
        } else {
            this.externalErrorFields = new Set();
            this.checkingFields = new Set();
            this.fieldEntities.forEach(entity => {
                this.notifyField(entity.getNamePath());
            });
        }

        this.forceRootUpdate();
    };

    private setFields = (fields: FieldData[]) => {
        const affectedFields = new Set<string>();
        
        fields.forEach(field => {
            const name = field.name;
            const nameStr = this.getNamePathStr(name);
            affectedFields.add(nameStr);

            if (field.value !== undefined) {
                this.setValueToStore(nameStr, field.value, this.store);
            }

            const meta = this.getFieldMeta(name);
            if (field.errors !== undefined) {
                meta.errors = field.errors;
                if (field.errors.length > 0) {
                    this.externalErrorFields.add(nameStr);
                } else {
                    this.externalErrorFields.delete(nameStr);
                }
            }
            if (field.touched !== undefined) meta.touched = field.touched;
            if (field.validating !== undefined) meta.validating = field.validating;
        });

        affectedFields.forEach(nameStr => {
            const name = nameStr.split('.');
            this.notifyField(name);
        });
        this.forceRootUpdate();
    };

    private setFieldValue = (name: NamePath, value: any) => {
        this.setFieldsValue({ [this.getNamePathStr(name)]: value });
    };

    private setFieldsValue = (values: Store) => {
        const affectedFields = new Set<string>();

        Object.keys(values).forEach(key => {
            this.setValueToStore(key, values[key], this.store);
            affectedFields.add(key);
        });

        affectedFields.forEach(nameStr => {
            const name = nameStr.split('.');
            this.notifyField(name);
        });

        const changedValues = values;
        const allValues = this.getFieldsValue(true);
        if (this.callbacks.onValuesChange) {
            this.callbacks.onValuesChange(changedValues, allValues);
        }
        this.forceRootUpdate();
    };

    private validateFields = async (nameList?: NamePath[]) => {
        const fields = nameList || this.fieldEntities.map(e => e.getNamePath());
        const promises = fields.map(name => this.validateField(name));

        try {
            await Promise.all(promises);
            return this.getFieldsValue(true);
        } catch (e) {
            const errorFields = fields
                .map(name => ({ name, errors: this.getFieldError(name) }))
                .filter(item => item.errors.length > 0);

            throw {
                values: this.getFieldsValue(true),
                errorFields,
                outOfDate: false,
            };
        }
    };

    private validateField = async (name: NamePath) => {
        const entity = this.getFieldEntity(name);
        if (!entity) return;

        const { rules } = entity.props;
        if (!rules || !rules.length) return;

        const value = this.getFieldValue(name);
        const meta = this.getFieldMeta(name);
        const currentSeq = ++meta.validateSeq;

        meta.validating = true;
        this.notifyField(name);

        try {
            await this.internalValidate(rules, value, currentSeq, meta);
            
            if (currentSeq === meta.validateSeq) {
                meta.errors = [];
            }
        } catch (err: any) {
            if (currentSeq === meta.validateSeq) {
                meta.errors = [err.message || String(err)];
            }
            throw err;
        } finally {
            if (currentSeq === meta.validateSeq) {
                meta.validating = false;
                meta.touched = true;
                this.notifyField(name);
            }
        }
    };

    private internalValidate = async (rules: Rule[], value: any, validateSeq: number, meta: FieldMeta) => {
        const hasNumericRule = rules.some(r => r.type === 'number' || r.type === 'integer' || r.type === 'float');

        for (const rule of rules) {
            if (validateSeq !== meta.validateSeq) {
                return;
            }

            if (rule.required && !value && value !== 0 && value !== false) {
                throw new Error(rule.message || 'Required');
            }

            const ruleType = rule.type;
            const isNumber = typeof value === 'number' || ruleType === 'number' || (hasNumericRule && !ruleType);


            if (rule.min) {
                if (isNumber) {
                    if (Number(value) < rule.min) throw new Error(rule.message || `Min ${rule.min}`);
                } else if (String(value).length < rule.min) {
                    throw new Error(rule.message || `Min length ${rule.min}`);
                }
            }
            if (rule.max) {
                if (isNumber) {
                    if (Number(value) > rule.max) throw new Error(rule.message || `Max ${rule.max}`);
                } else if (String(value).length > rule.max) {
                    throw new Error(rule.message || `Max length ${rule.max}`);
                }
            }

            if (rule.pattern && value && !rule.pattern.test(value)) {
                throw new Error(rule.message || 'Pattern mismatch');
            }
            if (rule.type === 'email' && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value))) {
                throw new Error(rule.message || 'Invalid email');
            }
            if (rule.type === 'number' && isNaN(Number(value))) {
                throw new Error(rule.message || 'Must be number');
            }
            if (rule.validator) {
                try {
                    const res = rule.validator(rule, value);
                    if (res instanceof Promise) {
                        await res;
                        if (validateSeq !== meta.validateSeq) {
                            return;
                        }
                    }
                } catch (err: any) {
                    if (validateSeq !== meta.validateSeq) {
                        return;
                    }
                    throw new Error(err.message || String(err));
                }
            }
        }
    };

    private submit = () => {
        this.validateFields()
            .then(values => {
                if (this.callbacks.onFinish) {
                    this.callbacks.onFinish(values);
                }
            })
            .catch(e => {
                if (this.scrollToFirstError && e.errorFields && e.errorFields.length > 0) {
                    this.scrollToField(e.errorFields[0].name);
                }

                if (this.callbacks.onFinishFailed) {
                    this.callbacks.onFinishFailed(e);
                }
            });
    };

    public scrollToField = (name: NamePath, options?: ScrollIntoViewOptions) => {
        const namePathStr = this.getNamePathStr(name);
        const entities = this.fieldEntities.filter(e => this.getNamePathStr(e.getNamePath()) === namePathStr);
        const entity = entities.find(e => e.getElement && e.getElement()) || entities[0];
        
        if (entity && entity.getElement) {
            const element = entity.getElement();
            if (element) {
                element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                    inline: 'nearest',
                    ...options,
                });
            }
        }
    };

    private registerField = (entity: FieldEntity) => {
        this.fieldEntities.push(entity);
        const name = entity.getNamePath();
        const nameStr = this.getNamePathStr(name);

        if (this.getValueFromStore(nameStr, this.store) === undefined) {
            const initVal = this.getValueFromStore(nameStr, this.initialValues);
            if (initVal !== undefined) {
                this.setValueToStore(nameStr, initVal, this.store);
            } else if (entity.props.initialValue !== undefined) {
                this.setValueToStore(nameStr, entity.props.initialValue, this.store);
            }
        }

        if (!this.fieldMeta[nameStr]) {
            this.fieldMeta[nameStr] = {
                touched: false,
                validating: false,
                errors: [],
                validateSeq: 0
            };
        }

        return () => {
            this.unregisterField(name);
        };
    };

    private unregisterField = (name: NamePath) => {
        const nameStr = this.getNamePathStr(name);
        
        this.fieldEntities = this.fieldEntities.filter(e => {
            const entityNameStr = this.getNamePathStr(e.getNamePath());
            return entityNameStr !== nameStr;
        });

        if (!this.preserve) {
            this.deleteValueFromStore(nameStr, this.store);
            delete this.fieldMeta[nameStr];
            this.notifyField(name);
            this.fieldListeners.delete(nameStr);
        }
    };

    private setCallbacks = (callbacks: Callbacks) => {
        this.callbacks = callbacks;
    };

    private setValidateMessages = (messages?: ValidateMessages) => {
        this.validateMessages = messages;
    };

    private setPreserve = (preserve?: boolean) => {
        this.preserve = preserve;
    };

    private getFields = () => {
        return this.fieldEntities.map(e => {
            const name = e.getNamePath();
            const meta = this.getFieldMeta(name);
            return {
                name,
                value: this.getFieldValue(name),
                touched: meta.touched,
                validating: meta.validating,
                errors: meta.errors
            };
        });
    };

    private getRegisteredFieldNames = (): string[] => {
        return this.fieldEntities
            .map(entity => entity.getNamePath())
            .filter((name): name is NamePath => name !== undefined && name !== null && name !== '')
            .map(name => this.getNamePathStr(name));
    };

    subscribe = (name: NamePath, listener: Listener): (() => void) => {
        const key = this.getNamePathStr(name);

        if (!this.fieldListeners.has(key)) {
            this.fieldListeners.set(key, new Set());
        }

        this.fieldListeners.get(key)!.add(listener);

        return () => {
            this.fieldListeners.get(key)?.delete(listener);
        };
    };

    private notifyField = (name: NamePath) => {
        const namePathStr = this.getNamePathStr(name);
        if (namePathStr === '') return;

        this.fieldListeners.forEach((listeners, key) => {
            if (namePathStr === key || namePathStr.startsWith(key + '.') || key.startsWith(namePathStr + '.')) {
                listeners.forEach(listener => listener());
            }
        });
    };

    private getFieldMeta = (name: NamePath): FieldMeta => {
        const nameStr = this.getNamePathStr(name);
        if (!this.fieldMeta[nameStr]) {
            this.fieldMeta[nameStr] = { 
                touched: false, 
                validating: false, 
                errors: [],
                validateSeq: 0
            };
        }
        return this.fieldMeta[nameStr];
    };

    private getFieldEntity = (name: NamePath) => {
        const nameStr = this.getNamePathStr(name);
        return this.fieldEntities.find(e => this.getNamePathStr(e.getNamePath()) === nameStr);
    };

    private getNamePathStr = (name: NamePath) => {
        return Array.isArray(name) ? name.join('.') : String(name);
    };

    private getValueFromStore = (pathStr: string, store: Store) => {
        const paths = pathStr.split('.');
        let current = store;
        for (let i = 0; i < paths.length; i++) {
            if (current === undefined) return undefined;
            current = current[paths[i]];
        }
        return current;
    };

    private setValueToStore = (pathStr: string, value: any, store: Store) => {
        const paths = pathStr.split('.');
        let current = store;
        for (let i = 0; i < paths.length - 1; i++) {
            const path = paths[i];
            if (current[path] === undefined) {
                const nextPath = paths[i + 1];
                current[path] = /^\d+$/.test(nextPath) ? [] : {};
            }
            current = current[path];
        }
        current[paths[paths.length - 1]] = value;
    };

    private deleteValueFromStore = (pathStr: string, store: Store) => {
        const paths = pathStr.split('.');
        let current = store;
        for (let i = 0; i < paths.length - 1; i++) {
            if (current[paths[i]] === undefined) {
                return;
            }
            current = current[paths[i]];
        }
        delete current[paths[paths.length - 1]];
    };
}
